import React, { useEffect, useState } from 'react';
import HeroBgImage from '../images/HeroBgImage2.jpg';
import { FiSearch } from 'react-icons/fi';
import { FaTruck, FaShieldAlt, FaStar, FaBolt, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';

const ORANGE = '#ff6701';
const BASE   = axiosInstance.defaults.baseURL;

/* ─── CSS keyframes injected once ─────────────────────── */
const STYLES = `
  @keyframes heroScroll {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(28px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes pulse-ring {
    0%   { box-shadow: 0 0 0 0   rgba(255,103,1,0.6); }
    70%  { box-shadow: 0 0 0 12px rgba(255,103,1,0);   }
    100% { box-shadow: 0 0 0 0   rgba(255,103,1,0);    }
  }
  .hero-marquee-track { display: flex; animation: heroScroll 28s linear infinite; }
  .hero-marquee-track:hover { animation-play-state: paused; }
  .hero-product-card {
    flex-shrink: 0; width: 170px; margin-right: 14px;
    background: rgba(255,255,255,0.12);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 14px; overflow: hidden;
    cursor: pointer; transition: transform 0.22s, box-shadow 0.22s;
  }
  .hero-product-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.35);
  }
  .hero-suggestion-item:hover { background: #fff8f4; color: #ff6701; }
`;

const HeroSection = () => {
  const [searchTerm, setSearchTerm]   = useState('');
  const [products, setProducts]       = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [focused, setFocused]         = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axiosInstance.get('/Products')
      .then(r => setProducts(r.data?.products || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const q = searchTerm.trim().toLowerCase();
    setSuggestions(
      q ? products.filter(p => p.title.toLowerCase().includes(q)).slice(0, 6) : []
    );
  }, [searchTerm, products]);

  const go    = () => navigate(`/product${searchTerm.trim() ? `?search=${encodeURIComponent(searchTerm)}` : ''}`);
  const goId  = (id) => navigate(`/product/${id}`);
  const goSug = (title) => { setSearchTerm(title); navigate(`/product?search=${encodeURIComponent(title)}`); };

  /* Duplicate products so marquee scrolls seamlessly */
  const doubled = [...products, ...products];

  return (
    <>
      <style>{STYLES}</style>

      <section style={s.hero}>
        {/* Background */}
        <img src={HeroBgImage} alt="" style={s.bg} />
        <div style={s.overlay} />
        {/* Decorative gradient orbs */}
        <div style={{ ...s.orb, top: '15%', left: '5%',  background: 'radial-gradient(circle, rgba(255,103,1,0.25) 0%, transparent 70%)' }} />
        <div style={{ ...s.orb, bottom: '10%', right: '8%', background: 'radial-gradient(circle, rgba(254,189,105,0.2) 0%, transparent 70%)' }} />

        {/* ── Content ── */}
        <div style={s.content}>

          {/* Caption pill */}
          <div style={s.pill}>
            <FaStar size={11} color="#febd69" style={{ marginRight: 5 }} />
            India's Trusted Spiritual Store
          </div>

          {/* Heading */}
          <h1 style={s.heading}>
            தெய்வீகம் வழிகாட்டும்,<br />
            <span style={{ color: ORANGE }}>வைதீகம் வணங்கும்</span>
          </h1>

          {/* Sub-heading */}
          <p style={s.sub}>
            Shop by Intention: <strong style={{ color: '#febd69' }}>Protection &amp; Positivity</strong>,{' '}
            Career &amp; Wealth, Love &amp; Relationships, Peace &amp; Meditation
          </p>

          {/* Trust pills */}
          <div style={s.trustRow}>
            {[
              { Icon: FaTruck,     text: 'Free Delivery' },
              { Icon: FaShieldAlt, text: '100% Genuine' },
              { Icon: FaStar,      text: '4.8 ★ Rated' },
            ].map(({ Icon, text }) => (
              <div key={text} style={s.trustPill}>
                <Icon size={12} color={ORANGE} style={{ marginRight: 5 }} />{text}
              </div>
            ))}
          </div>

          {/* ── Search bar ── */}
          <div style={s.searchWrap}>
            <div style={s.searchBox}>
              <FiSearch size={18} color="#aaa" style={{ marginLeft: 14, flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search for products, intentions, categories…"
                style={s.searchInput}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && go()}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
              />
              <button style={s.searchBtn} onClick={go}>
                Search <FaArrowRight size={12} style={{ marginLeft: 6 }} />
              </button>
            </div>

            {/* Suggestions dropdown */}
            {focused && suggestions.length > 0 && (
              <ul style={s.dropdown}>
                {suggestions.map(p => (
                  <li
                    key={p._id}
                    className="hero-suggestion-item"
                    style={s.dropItem}
                    onMouseDown={() => goSug(p.title)}
                  >
                    <FiSearch size={12} color="#ccc" style={{ marginRight: 8, flexShrink: 0 }} />
                    {p.title}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* CTA buttons */}
          <div style={s.ctaRow}>
            <button style={s.primaryCta} onClick={() => navigate('/product')}>
              <FaBolt size={14} style={{ marginRight: 7 }} /> Shop Now
            </button>
            <button style={s.secondaryCta} onClick={() => navigate('/consultationPage')}>
              Get Consultation
            </button>
          </div>
        </div>

        {/* ── Product marquee strip ── */}
        <div style={s.marqueeWrap}>
          <p style={s.marqueeLabel}>✨ Featured Products</p>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ width: 28, height: 28, border: '3px solid rgba(255,255,255,0.2)', borderTop: `3px solid ${ORANGE}`, borderRadius: '50%', animation: 'heroScroll 0.8s linear infinite', display: 'inline-block' }} />
            </div>
          ) : (
            <div style={{ overflow: 'hidden' }}>
              <div className="hero-marquee-track">
                {doubled.map((p, i) => {
                  const price = p.offerPrice > 0 ? p.offerPrice : p.CurrentPrice;
                  const img   = `${BASE}${p.images?.[0]?.url}`;
                  return (
                    <div
                      key={`${p._id}-${i}`}
                      className="hero-product-card"
                      onClick={() => goId(p._id)}
                    >
                      <div style={{ position: 'relative', backgroundColor: 'rgba(255,255,255,0.08)', height: 120 }}>
                        <img src={img} alt={p.title} style={s.cardImg} />
                        <span style={s.priceBadge}>₹{price}</span>
                      </div>
                      <div style={s.cardBody}>
                        <p style={s.cardTitle}>{p.title}</p>
                        <button style={s.cardBtn}>Buy Now</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

/* ──────────────── Inline styles ─────────────────────────── */
const s = {
  hero: {
    position: 'relative', width: '100%',
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    overflow: 'hidden', fontFamily: '"Rubik", "Poppins", sans-serif',
  },
  bg: {
    position: 'absolute', inset: 0, width: '100%', height: '100%',
    objectFit: 'cover', zIndex: 0,
    filter: 'brightness(0.5) saturate(0.8)',
  },
  overlay: {
    position: 'absolute', inset: 0, zIndex: 1,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 60%, rgba(10,10,10,0.95) 100%)',
  },
  orb: {
    position: 'absolute', zIndex: 1,
    width: 500, height: 500, borderRadius: '50%',
    pointerEvents: 'none',
  },
  content: {
    position: 'relative', zIndex: 100, flex: 1,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '80px 20px 20px',
    textAlign: 'center',
    animation: 'fadeUp 0.9s ease both',
  },
  pill: {
    display: 'inline-flex', alignItems: 'center',
    backgroundColor: 'rgba(255,103,1,0.2)',
    border: '1px solid rgba(255,103,1,0.4)',
    color: '#febd69', fontSize: 12, fontWeight: 600,
    padding: '6px 14px', borderRadius: 20, marginBottom: 20,
    backdropFilter: 'blur(8px)',
    animation: 'pulse-ring 2.4s ease infinite',
  },
  heading: {
    fontSize: 'clamp(24px, 5vw, 46px)',
    fontWeight: 800, color: '#fff', lineHeight: 1.25,
    margin: '0 0 16px', textShadow: '0 2px 20px rgba(0,0,0,0.5)',
    maxWidth: 700,
  },
  sub: {
    fontSize: 'clamp(13px, 1.8vw, 16px)',
    color: 'rgba(255,255,255,0.8)', lineHeight: 1.7,
    maxWidth: 580, margin: '0 auto 20px',
  },
  trustRow: { display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 28 },
  trustPill: {
    display: 'flex', alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#fff', fontSize: 12, fontWeight: 600,
    padding: '6px 12px', borderRadius: 20,
  },
  /* Search */
  searchWrap: { position: 'relative', width: '100%', maxWidth: 580, marginBottom: 24, zIndex: 9999 },
  searchBox: {
    display: 'flex', alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderRadius: 50, overflow: 'visible',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    fontSize: 14, padding: '16px 14px',  color: '#333',
    fontFamily: 'inherit',
  },
  searchBtn: {
    display: 'inline-flex', alignItems: 'center',
    backgroundColor: ORANGE, color: '#fff',
    border: 'none', borderRadius: 50,
    padding: '12px 22px', fontWeight: 700, fontSize: 13,
    cursor: 'pointer', margin: 4, fontFamily: 'inherit',
    whiteSpace: 'nowrap', flexShrink: 0,
    boxShadow: '0 4px 14px rgba(255,103,1,0.5)',
  },
  dropdown: {
    position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
    backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
    listStyle: 'none', margin: 0, padding: '6px 0', zIndex: 10000,
  },
  dropItem: {
    display: 'flex', alignItems: 'center',
    padding: '11px 16px', fontSize: 13, color: '#333',
    cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
  },
  /* CTA */
  ctaRow: { display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 40 },
  primaryCta: {
    display: 'inline-flex', alignItems: 'center',
    backgroundColor: ORANGE, color: '#fff',
    border: 'none', borderRadius: 10,
    padding: '13px 28px', fontWeight: 700, fontSize: 14,
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 6px 20px rgba(255,103,1,0.5)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  secondaryCta: {
    display: 'inline-flex', alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
    backdropFilter: 'blur(8px)',
    border: '1.5px solid rgba(255,255,255,0.3)', color: '#fff',
    borderRadius: 10, padding: '12px 24px',
    fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
    transition: 'background 0.2s',
  },
  /* Marquee */
  marqueeWrap: {
    position: 'relative', zIndex: 1,
    borderTop: '1px solid rgba(255,255,255,0.08)',
    padding: '16px 0 24px',
    background: 'linear-gradient(0deg, rgba(10,10,10,0.98) 0%, transparent 100%)',
  },
  marqueeLabel: {
    textAlign: 'center', color: 'rgba(255,255,255,0.55)',
    fontSize: 12, fontWeight: 600, letterSpacing: 1,
    textTransform: 'uppercase', margin: '0 0 12px',
  },
  /* Cards */
  cardImg: {
    width: '100%', height: '100%', objectFit: 'contain',
    padding: 10, display: 'block',
  },
  priceBadge: {
    position: 'absolute', bottom: 6, right: 8,
    backgroundColor: ORANGE, color: '#fff',
    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
  },
  cardBody: { padding: '10px 10px 12px' },
  cardTitle: {
    fontSize: 11, color: '#fff', fontWeight: 600,
    margin: '0 0 8px', lineHeight: 1.3,
    overflow: 'hidden', display: '-webkit-box',
    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  },
  cardBtn: {
    width: '100%', padding: '7px 0',
    backgroundColor: ORANGE, color: '#fff',
    border: 'none', borderRadius: 8,
    fontWeight: 700, fontSize: 11, cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default HeroSection;
